import { useState } from 'react';
import type { ImageRef } from '~/content/types';
import { cn } from '~/lib/cn';

/**
 * Renders the referenced asset; falls back to a styled placeholder while the
 * real export from Figma has not landed in /public/images yet.
 */
export function SmartImage({
  image,
  className,
  loading = 'lazy',
  fetchPriority,
}: {
  image: ImageRef;
  className?: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
}) {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <div
        role="img"
        aria-label={image.alt}
        className={cn(
          'flex items-center justify-center bg-grey-100 text-center text-body-s text-grey-500',
          className,
        )}>
        <span className="max-w-[80%] px-4">{image.alt}</span>
      </div>
    );
  }

  const img = (
    <img
      // With SSR the error event can fire before hydration, so also check
      // whether the image already failed once the node mounts.
      ref={(node) => {
        if (node && node.complete && node.naturalWidth === 0) setErrored(true);
      }}
      src={image.src}
      alt={image.alt}
      width={image.width}
      height={image.height}
      sizes={image.sizes}
      loading={loading}
      fetchPriority={fetchPriority}
      decoding="async"
      onError={() => setErrored(true)}
      className={cn('object-cover', className)}
    />
  );

  // Serve WebP and/or a mobile-specific image via <picture>, keeping the
  // original `src` as the ultimate fallback. Sources are ordered most- to
  // least-specific: mobile below `md` (< 768px), then desktop WebP, then the
  // plain <img>. `contents` keeps the <picture> out of the layout box so the
  // <img>'s sizing/positioning is unchanged.
  const mobileMedia = '(max-width: 767.98px)';
  const hasSources = image.mobileWebpSrc || image.mobileSrc || image.webpSrc;

  if (hasSources) {
    return (
      <picture className="contents">
        {image.mobileWebpSrc && (
          <source
            media={mobileMedia}
            type="image/webp"
            srcSet={image.mobileWebpSrc}
            sizes={image.sizes}
          />
        )}
        {image.mobileSrc && (
          <source media={mobileMedia} srcSet={image.mobileSrc} sizes={image.sizes} />
        )}
        {image.webpSrc && <source type="image/webp" srcSet={image.webpSrc} sizes={image.sizes} />}
        {img}
      </picture>
    );
  }

  return img;
}
